/**
 * Convert a USD amount by the shop's exchange rate and round UP to a
 * magnitude-aware step, so large-denomination currencies (JPY, KRW) land on
 * clean numbers instead of an arbitrary nearest-5.
 *
 * Fallback copy of the canonical implementation in erj-utils.js — keep in sync.
 * The dynamic step keeps ~3 significant figures snapped to a 0/5 grid, minimum 5:
 *   step = 5 × 10^max(0, floor(log10(value)) - 2)
 *   e.g. 46 → step 5 → 50, 6750 → step 50 → 6750, 47250 → step 500 → 47500.
 * Pass an explicit positive `step` to override the dynamic behaviour.
 *
 * @param {number} usdAmount
 * @param {number} [step] - optional fixed step; omit for the magnitude-aware step
 * @returns {number}
 */
if (!window._erj_.convertUsdAndRoundUp) {
  window._erj_.convertUsdAndRoundUp = function (usdAmount, step) {
    const parsedAmount = Number(usdAmount);
    const parsedRate = Number(window.Shopify?.currency?.rate || 1);
    if (!Number.isFinite(parsedAmount) || !Number.isFinite(parsedRate)) return NaN;
    const converted = parsedAmount * parsedRate;
    if (!(converted > 0)) return 0;
    const explicitStep = Number(step);
    const roundStep =
      Number.isFinite(explicitStep) && explicitStep > 0
        ? explicitStep
        : 5 * Math.pow(10, Math.max(0, Math.floor(Math.log10(converted)) - 2));
    return Math.ceil(converted / roundStep) * roundStep;
  };
}

function convertAndFormatEdge(edges) {
  if (!Array.isArray(edges)) return;
  
  edges.forEach((item) => {
    if (!item) return;
    if (item.thresholdType === "count") return; // do not convert count
    if (item.presentment === true) return; // already in presentment currency (free-shipping-threshold `local` mode)

    const amount = Number(item.edge) || 0;
    const converted = Number(window._erj_.convertUsdAndRoundUp(amount));
    
    if (!Number.isFinite(converted)) {
      item.edge = 0;
    } else if (Number.isInteger(converted)) {
      item.edge = converted;
    } else {
      item.edge = Number(converted.toFixed(2));
    }
  });
}

if (typeof pbObject !== "undefined") {
  convertAndFormatEdge(pbObject?.edges);
}
if (typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject)) {
  convertAndFormatEdge(freeGiftObject);
  freeGiftObject.forEach(gift => {
    if (gift && Array.isArray(gift.products)) {
      if (gift.products.length === 1 && !gift.products[0].selected) {
        gift.products[0].selected = true;
      }
    }
  });
}

// Calculate eligible cart sum (excluding free gifts and gift cards), net of
// discounts. The gift threshold must be judged on what the customer actually
// pays, so both product-level (line) and order-level (cart) discounts are
// subtracted. Shopify's AJAX line item `total_discount` already aggregates
// every allocation on the line — line-level discounts plus the line's share of
// order-level discounts — which is why `original_line_price - total_discount`
// yields the fully-discounted line total. When a discount later drops the cart
// below a tier, that tier stops qualifying and runFreeGiftsByEvent removes the
// now-ineligible free gift.
function getEligibleCartSum(cart) {
  return (cart?.items || [])
    .filter((item) => item?.properties?._gift !== "free_gift" && !item?.gift_card)
    .reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      // `original_line_price` may be formatted to a string upstream (main.js),
      // so rebuild the pre-discount line total from numeric unit fields.
      const originalUnit = Number(item.original_price);
      const originalLine = Number.isFinite(originalUnit)
        ? originalUnit * quantity
        : (Number(item.price) || 0) * quantity;
      const discount = Number(item.total_discount) || 0;
      const net = originalLine - discount;
      return sum + (net > 0 ? net : 0);
    }, 0);
}

const EXCLUDED_PRODUCT_TYPES = [
  "gid://shopify/Metaobject/176606380098", // T-shirt
  "gid://shopify/Metaobject/156988964930", // Jewelry Storage
  "gid://shopify/Metaobject/156988932162", // Packages
  "gid://shopify/Metaobject/156988899394", // Earrings Back Lifters
  "gid://shopify/Metaobject/156988866626", // Tote Bags
  "gid://shopify/Metaobject/156988801090", // Gift Boxes
  "gid://shopify/Metaobject/156988768322", // Hair Accessories
  "gid://shopify/Metaobject/156988735554", // Extenders
  "gid://shopify/Metaobject/156988637250", // Keychains
  "gid://shopify/Metaobject/156988604482", // Bag Chains
  "gid://shopify/Metaobject/156988571714", // Gift Cards
];

// The `custom.product_types` metaobject-reference GIDs are server-rendered onto
// each cart-items line (data-product-types, snippets/cart-items.liquid) rather
// than fetched here — <cart-items> lives in the layout so it's on every page,
// and its markup is always refreshed before `enroute:cart-updated` fires (see
// assets/cart.js's requestCartUpdate and assets/global.js's mutateCart), so the
// DOM is guaranteed current by the time this reads it.
function getProductTypesByLineKey() {
  const map = new Map();
  if (typeof document === "undefined") return map;
  document.querySelectorAll("cart-items [data-cart-item]").forEach((el) => {
    const key = el.dataset.lineKey;
    if (!key) return;
    try {
      map.set(key, JSON.parse(el.dataset.productTypes || "null"));
    } catch (e) {
      map.set(key, null);
    }
  });
  return map;
}

// Calculate eligible cart count (excluding free gifts and gift cards and not jewelry products)
function getEligibleCartCount(cart) {
  const productTypesByLineKey = getProductTypesByLineKey();
  return (cart?.items || [])
    .filter((item) => {
      const isExcluded = item?.properties?._gift === "free_gift" || item?.gift_card;
      const types = productTypesByLineKey.get(item.key);
      const hasExcludedType = Array.isArray(types) && types.some((t) => EXCLUDED_PRODUCT_TYPES.includes(t));
      return !isExcluded && !hasExcludedType;
    })
    .reduce((count, item) => count + (Number(item.quantity) || 0), 0);
}

/** Promo Bar Base Class */
(function () {
  class BasePromoProgressBar extends HTMLElement {
    constructor() {
      super();
      this._cartState = null;
      this._updateHandler = this._onCartUpdate.bind(this);
    }

    connectedCallback() {
      window.addEventListener("enroute:cart-updated", this._updateHandler);
      this._config = this.initConfig();

      if (window.__enroutePromoBarState && window.__enroutePromoBarState.cart) {
        this._cartState = window.__enroutePromoBarState.cart;
        this.updateView();
      }
    }

    disconnectedCallback() {
      window.removeEventListener("enroute:cart-updated", this._updateHandler);
    }

    _onCartUpdate(event) {
      if (!event || !event.detail || !event.detail.cart) return;
      this.refresh(event.detail.cart);
    }

    /** Public refresh entry point for callers that hold the cart directly
     * (e.g. cart-popup.js), so they can update this element without going
     * through the `enroute:cart-updated` broadcast. */
    refresh(cart) {
      if (!cart) return;
      this._cartState = cart;
      this.updateView();
    }

    updateView() {
      if (!this._cartState) return;
      this.renderUI(this._cartState);
    }

    getEligibleValue(cart, type = "amount") {
      // Account orders preview: use the same selection as its subtotal.
      if (this.dataset.selectionAmount != null) {
        return type === "count"
          ? Number(this.dataset.selectionCount) || 0
          : Number(((Number(this.dataset.selectionAmount) || 0) / 100).toFixed(2));
      }
      if (type === "count") {
        return typeof getEligibleCartCount === "function" ? getEligibleCartCount(cart) : (cart.item_count || 0);
      }
      const cartSum = typeof getEligibleCartSum === "function" ? getEligibleCartSum(cart) : (cart.total_price || 0);
      return Number((cartSum / 100).toFixed(2));
    }

    initConfig() {
      return [];
    }

    renderUI(cartState) {
      console.warn("renderUI must be implemented by subclass");
    }
  }

  window.BasePromoProgressBar = BasePromoProgressBar;
})();

/** Promo Bar by Amount */
(function () {
  if (!window.BasePromoProgressBar) return console.error("BasePromoProgressBar not found");

  var _PromoBar_instances,
    getEdges_fn,
    formatMoney_fn,
    resolveMessage_fn,
    escapeHtml_fn;

  var PromoBar = class extends window.BasePromoProgressBar {
    constructor() {
      super();
      _PromoBar_instances.add(this);
    }

    connectedCallback() {
      super.connectedCallback();
      // The desktop-single-row override depends on the viewport, so re-render
      // when crossing the desktop breakpoint.
      if (this.dataset.desktopSingleRow === "true") {
        this._onDesktopBreakpointChange = () => this.updateView();
        PromoBar.DESKTOP_MEDIA.addEventListener("change", this._onDesktopBreakpointChange);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this._onDesktopBreakpointChange) {
        PromoBar.DESKTOP_MEDIA.removeEventListener("change", this._onDesktopBreakpointChange);
        this._onDesktopBreakpointChange = null;
      }
    }

    initConfig() {
      return typeof pbObject !== "undefined" ? pbObject : null;
    }

    renderUI(cartState) {
      const barObject = this._config;
      if (!barObject || !barObject.edges || !barObject.edges.length) {
        this.innerHTML = "";
        return;
      }
      const edges = __privateMethodPromoBar(this, _PromoBar_instances, getEdges_fn).call(this, barObject);
      if (!edges.length) {
        this.innerHTML = "";
        return;
      }
      const thresholdType = edges[0].thresholdType;
      const crossedValue = this.getEligibleValue(cartState, thresholdType);
      const maxEdge = edges[edges.length - 1].value;
      const isUnlocked = crossedValue >= maxEdge;

      // In contexts like the add-to-cart confirmation toast a fully-unlocked
      // bar adds no value, so hide it entirely once the top threshold is met.
      if (this.dataset.hideWhenMaxed === "true" && isUnlocked) {
        this.innerHTML = "";
        return;
      }
      const message = __privateMethodPromoBar(this, _PromoBar_instances, resolveMessage_fn).call(this, crossedValue, edges, barObject, thresholdType);

      const tierCount = edges.length;

      // Layout comes from the theme setting (pbObject.tierLayout): "auto"
      // (default) wraps 5+ tiers onto two rows because evenly-spaced nodes
      // crowd the single rail, "two_rows" always wraps, "single" never does.
      // data-desktop-single-row (account Shopping Cart) pins desktop to one
      // row — its wide layout fits every tier on a single rail. Two rows:
      // the first row carries 3 tiers, the second the remaining ones, as
      // consecutive segments of the same value scale — row 2's 0% sits at
      // row 1's last tier value. With ≤3 tiers there is nothing to wrap, so
      // any mode falls back to a single row.
      const desktopSingleRow =
        this.dataset.desktopSingleRow === "true" && PromoBar.DESKTOP_MEDIA.matches;
      const tierLayout = desktopSingleRow ? "single" : barObject.tierLayout || "auto";
      const splitRows =
        tierLayout === "two_rows" ? tierCount > 3 : tierLayout === "single" ? false : tierCount > 4;
      const rows = splitRows ? [edges.slice(0, 3), edges.slice(3)] : [edges];

      // Within a row, nodes are distributed evenly along its rail: the start
      // sits at 0% and every edge takes an equal slot, regardless of its
      // threshold value. The fill is piecewise-linear so the bar still lines
      // up with those evenly-spaced nodes: interpolate within whichever value
      // segment the cart sits in.
      let rowStartValue = 0;
      const rowStates = rows.map((rowEdges) => {
        const rowCount = rowEdges.length;
        const edgePercent = (index) => ((index + 1) / rowCount) * 100;

        let fillPercent = 0;
        if (crossedValue >= rowEdges[rowCount - 1].value) {
          fillPercent = 100;
        } else if (crossedValue > rowStartValue) {
          let prevValue = rowStartValue;
          let prevPercent = 0;
          for (let i = 0; i < rowCount; i++) {
            const curValue = rowEdges[i].value;
            const curPercent = edgePercent(i);
            if (crossedValue < curValue) {
              const span = curValue - prevValue;
              const ratio = span > 0 ? (crossedValue - prevValue) / span : 1;
              fillPercent = prevPercent + ratio * (curPercent - prevPercent);
              break;
            }
            prevValue = curValue;
            prevPercent = curPercent;
          }
        }
        if (fillPercent > 100) fillPercent = 100;
        if (fillPercent < 0) fillPercent = 0;

        const state = { rowEdges, edgePercent, fillPercent, rowStartValue };
        rowStartValue = rowEdges[rowCount - 1].value;
        return state;
      });

      const icon = isUnlocked ? this.dataset.iconUnlocked || "" : this.dataset.iconLocked || "";
      const escape = __privateMethodPromoBar(this, _PromoBar_instances, escapeHtml_fn);

      // Single row with 5+ tiers staggers the labels around the rail — odd
      // tiers (1st, 3rd, …) below, even tiers (2nd, 4th, …) above — so
      // neighbouring labels on the crowded rail can't collide.
      const altLabels = rows.length === 1 && tierCount > 4;

      const lastFillPercents = this._lastFillPercents || [];
      const rowsHtml = rowStates
        .map((state, rowIndex) => {
          // The hidden 0% base tick seeds the "first real tier met" :has()
          // highlight; on follow-up rows it only counts as met once the
          // previous row is fully crossed.
          const baseTickMet = rowIndex === 0 || crossedValue >= state.rowStartValue;
          const ticksHtml =
            `<div class="tick${baseTickMet ? " met" : ""}" style="left:0%"></div>` +
            state.rowEdges
              .map((edge, index) => {
                const position = state.edgePercent(index);
                const met = crossedValue >= edge.value;
                return `<div class="tick${met ? " met" : ""}" style="left:${position}%"></div>`;
              })
              .join("");

          const labelHtml = (edge, index) => {
            const position = state.edgePercent(index);
            const done = crossedValue >= edge.value;
            const isRowLast = index === state.rowEdges.length - 1;
            const statusClass = (done ? "done" : "next") + (isRowLast ? " right" : "");
            return `<div class="l ${statusClass}" style="left:${position}%">${escape.call(this, edge.info)}</div>`;
          };

          const belowLabels = [];
          const aboveLabels = [];
          state.rowEdges.forEach((edge, index) => {
            (altLabels && index % 2 === 1 ? aboveLabels : belowLabels).push(labelHtml(edge, index));
          });

          return `
        ${altLabels ? `<div class="tier-labels tier-labels--above">${aboveLabels.join("")}</div>` : ""}
        <div class="tier-track" aria-hidden="true">
          <div class="rail"><div class="fill" style="width:${lastFillPercents[rowIndex] || 0}%"></div></div>
          <div class="ticks">
            ${ticksHtml}
          </div>
        </div>
        <div class="tier-labels">${belowLabels.join("")}</div>`;
        })
        .join("");

      this.innerHTML = `
      <div class="member-promo ${isUnlocked ? "unlocked" : "progress"}${rowStates.length > 1 ? " multi-row" : ""}${altLabels ? " alt-labels" : ""}" role="region" aria-label="Promo progress">
        <div class="member-promo__head">
          <div class="member-promo__title">
            <span class="icon" aria-hidden="true">${icon}</span>
              ${message.currentInfo || ""}
          </div>
        </div>
        ${rowsHtml}
      </div>
    `;

      // innerHTML rebuilds the .fill nodes every render, so a CSS width
      // transition has no "from" state to animate. Render at the previous
      // widths above, then bump to the targets on the next frame so the
      // transition fires.
      const fillPercents = rowStates.map((state) => state.fillPercent);
      const fillEls = this.querySelectorAll(".tier-track .rail .fill");
      if (fillEls.length) {
        requestAnimationFrame(() => {
          fillEls.forEach((fillEl, i) => {
            fillEl.style.width = fillPercents[i] + "%";
          });
        });
      }
      this._lastFillPercents = fillPercents;
    }
  };
  _PromoBar_instances = new WeakSet();
  getEdges_fn = function (barObject) {
    if (!Array.isArray(barObject?.edges)) return [];
    return barObject.edges
      .map((item) => {
        const value = Number(item?.edge);
        if (!Number.isFinite(value) || value <= 0) return null;
        return {
          value,
          thresholdType: item?.thresholdType || "amount",
          info: item?.info || "",
          before: item?.before || "",
          beforeSub: item?.beforeSub || "",
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.value - b.value);
  };
  escapeHtml_fn = function (value) {
    return String(value == null ? "" : value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };
  formatMoney_fn = function (cents) {
    const normalized = Number(cents) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(normalized).replace("<span class=money>", " ").replace("</span>", " ");
    }
    return normalized.toFixed(2);
  };
  resolveMessage_fn = function (value, edges, barObject, thresholdType = "amount") {
    const nextEdgeIndex = edges.findIndex((edge) => value < edge.value);
    if (nextEdgeIndex === -1) {
      return {
        currentInfo: barObject.finalInfo || "",
        currentSubInfo: barObject.finalSubInfo || "",
      };
    }
    const nextEdge = edges[nextEdgeIndex];
    const missing = __privateMethodPromoBar(this, _PromoBar_instances, formatMoney_fn).call(this, (nextEdge.value - value) * 100);
    return {
      currentInfo: nextEdge.before.replace("XX.XX", missing).replace("{{gap_money}}", missing),
      currentSubInfo: nextEdge.beforeSub.replace("XX.XX", missing).replace("{{gap_money}}", missing),
    };
  };
  // The account pages' own desktop breakpoint (customhvg-account.css).
  PromoBar.DESKTOP_MEDIA = window.matchMedia("(min-width: 1025px)");
  if (!window.customElements.get("promo-bar")) {
    window.customElements.define("promo-bar", PromoBar);
  }
  function __privateMethodPromoBar(obj, set, fn) {
    if (!set.has(obj)) {
      throw new TypeError("Cannot access private method");
    }
    return fn;
  }
})();

/** Progress Bar by Count */
(function () {
  if (!window.BasePromoProgressBar) return console.error("BasePromoProgressBar not found");

  var ProgressBarByCount = class extends window.BasePromoProgressBar {
    initConfig() {
      const configScript = this.querySelector('[data-rules-config]');
      if (configScript) {
        try {
          const rules = JSON.parse(configScript.textContent);
          return rules.sort((a, b) => a.threshold - b.threshold);
        } catch (e) {
          console.error("Failed to parse rules config", e);
        }
      }
      return [];
    }

    renderUI(cartState) {
      const rules = this._config;
      if (!rules || rules.length === 0) return;

      const itemCount = this.getEligibleValue(cartState, "count");
      this.dataset.itemCount = itemCount;

      const locales = window._erj_?.getGlobalLocaleStrings?.()?.member_promo || {
        progress: {
          added_count: "{{ current }} / {{ max }} added",
          items_node: "{{ count }} items · {{ text }}"
        }
      };

      const maxThreshold = parseInt(
        this.dataset.maxThreshold || rules[rules.length - 1].threshold.toString(),
        10,
      );

      const finalTitle = this.dataset.finalTitle;
      const finalSubtitle = this.dataset.finalSubtitle;

      let currentRule = null;
      for (const rule of rules) {
        if (itemCount < rule.threshold) {
          currentRule = rule;
          break;
        }
      }

      let currentTitle = "";
      let currentSubtitle = "";
      let currentNodeText = "";
      let countNeeded = 0;

      if (currentRule) {
        currentTitle = currentRule.title;
        currentSubtitle = currentRule.subtitle;
        currentNodeText = currentRule.nodeText;
        countNeeded = currentRule.threshold - itemCount;
      } else {
        currentTitle = finalTitle;
        currentSubtitle = finalSubtitle;
        currentNodeText = rules[rules.length - 1].nodeText;
        countNeeded = 0;
      }

      const replacementText = '<span class="member-promo__em">' + currentNodeText + '</span>';
      const formattedSubtitle = currentSubtitle
        .replace("{{gap_count}}", countNeeded)
        .replace(currentNodeText, replacementText);

      const wrapper = this.querySelector(".member-promo");
      if (!wrapper) return;

      const isLocked = wrapper.classList.contains("member-promo--locked");

      if (!isLocked) {
        if (itemCount >= maxThreshold) {
          wrapper.classList.remove("progress");
          wrapper.classList.add("unlocked");
        } else {
          wrapper.classList.remove("unlocked");
          wrapper.classList.add("progress");
        }
      }

      const titleEl = this.querySelector(".member-promo__title");
      if (titleEl && !isLocked) {
        const unlockIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M736 64c-123.52 0-224 100.5-224 224v96H128a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h512a64 64 0 0 0 64-64V448a64 64 0 0 0-64-64h-64v-96c0-88.22 71.78-160 160-160s160 71.78 160 160v96a32 32 0 0 0 64 0v-96c0-123.49-100.49-224-224-224zM416 631.12V800a32 32 0 0 1-64 0V631.12a64 64 0 1 1 64 0z"/></svg>`;
        const titleText = itemCount < maxThreshold ? currentTitle : finalTitle;
        titleEl.innerHTML = `
            <span class="icon" aria-hidden="true">${unlockIconSvg}</span>
            <strong>${titleText}</strong>
          `;
      }

      const countEl = this.querySelector(".member-promo__count");
      if (countEl && !isLocked) {
        countEl.innerHTML = locales.progress.added_count
          .replace("{{ current }}", itemCount)
          .replace("{{ max }}", maxThreshold);
      }

      const fillEl = this.querySelector(".tier-track .rail .fill");
      if (fillEl) {
        let fillPercent = (itemCount / maxThreshold) * 100;
        if (fillPercent > 100) fillPercent = 100;
        fillEl.style.width = fillPercent + "%";
      }

      const ticks = this.querySelectorAll(".tier-track .ticks .tick");
      for (let i = 0; i < rules.length; i++) {
        if (ticks[i + 1]) {
          ticks[i + 1].classList.toggle("met", itemCount >= rules[i].threshold);
        }
      }

      const labels = this.querySelectorAll(".tier-labels .l");
      for (let i = 0; i < rules.length; i++) {
        if (labels[i]) {
          const rule = rules[i];
          let labelHtml = locales.progress.items_node
            .replace("{{ count }}", rule.threshold)
            .replace("{{ text }}", rule.nodeText);
          const isLast = (i === rules.length - 1);
          if (itemCount >= rule.threshold) {
            labels[i].className = `l done${isLast ? " right" : ""}`;
            labels[i].innerHTML = `${labelHtml} &#10003;`;
          } else {
            labels[i].className = `l ${isLast ? "right" : "next"}`;
            labels[i].innerHTML = labelHtml;
          }
        }
      }

      const ctaEl = this.querySelector(".member-promo__cta");
      if (ctaEl && !isLocked) {
        if (itemCount < maxThreshold) {
          ctaEl.innerHTML = formattedSubtitle;
        } else {
          ctaEl.innerHTML = finalSubtitle;
        }
      }
    }
  };

  if (!window.customElements.get("progress-bar-by-count")) {
    window.customElements.define("progress-bar-by-count", ProgressBarByCount);
  }
})();

(function () {
  var _FreeGiftModals_instances,
    onFreeGiftUpdate_fn,
    escapeHtml_fn,
    getOptimizedImageSize_fn,
    setGiftSelected_fn,
    toggleGiftVariantSwither_fn,
    getGiftSelectedOption_fn,
    isGiftOptionAvailable_fn,
    isGiftOptionSelected_fn,
    setGiftSwitchOption_fn,
    isGiftValidAndSelected_fn,
    replaceFreeGiftImage_fn,
    setFreeGiftVidValue_fn,
    setFreeGiftPossibleOptionValues_fn,
    unlockFreeGiftElemets_fn,
    addFreeGiftToCart_fn,
    closeGiftModal_fn,
    getGiftByIndex_fn,
    getItemByIndex_fn,
    handleClick_fn,
    render_fn;
  var FreeGiftModals = class extends HTMLElement {
    constructor() {
      super();
      _FreeGiftModals_instances.add(this);
      this._onUpdate = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, onFreeGiftUpdate_fn).bind(this);
      this._onClick = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, handleClick_fn).bind(this);
    }
    connectedCallback() {
      const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
      gifts.forEach((gift) => {
        if (gift.benefitType === "variant_set") {
          gift.products.forEach((item) => {
            if (item.vid && item.product && Array.isArray(item.product.variants)) {
              item.product.variants = item.product.variants.filter((v) => v.id === item.vid);
            }
          });
        }
      });

      window.addEventListener("free-gift:update", this._onUpdate);
      this.addEventListener("click", this._onClick);
      __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
    }
    disconnectedCallback() {
      window.removeEventListener("free-gift:update", this._onUpdate);
      this.removeEventListener("click", this._onClick);
    }
  };
  _FreeGiftModals_instances = new WeakSet();
  onFreeGiftUpdate_fn = function () {
    __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
  };
  function calculateAvailableOptionsForItem(item) {
    if (!item.selectedOptions) item.selectedOptions = {};
    if (!item.availableOptionValues) item.availableOptionValues = {};

    item.options.forEach((opt, currentIndex) => {
      const variantOptionKey = `option${currentIndex + 1}`;

      // Find variants that match all selected options BEFORE the current index (Upper levels)
      const upperMatchedVariants = item.product.variants.filter((variant) => {
        return item.options.every((upperOpt, upperIndex) => {
          // Ignore current and lower levels
          if (upperIndex >= currentIndex) return true;

          const selectedValue = item.selectedOptions[upperOpt.name];
          if (selectedValue === undefined) return true;

          const upperVariantOptionKey = `option${upperIndex + 1}`;
          return variant[upperVariantOptionKey] === selectedValue;
        });
      });

      item.availableOptionValues[opt.name] = [
        ...new Set(
          upperMatchedVariants
            .filter((v) => v.available)
            .map((v) => v[variantOptionKey])
            .filter(Boolean),
        ),
      ];
    });
  }
  escapeHtml_fn = function (value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };
  getOptimizedImageSize_fn = function (url, width) {
    return `${url}&width=${width}`;
  };
  setGiftSelected_fn = function (gift, item) {
    for (let i = 0; i < gift.products.length; i++) {
      gift.products[i].selected = false;
    }
    item.selected = true;
  };
  toggleGiftVariantSwither_fn = function (item) {
    item.switcherVisible = !item.switcherVisible;
  };
  getGiftSelectedOption_fn = function (item) {
    const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
    let result = [];
    if (item.vid) {
      if (item.selectedOptions) {
        result = Object.values(item.selectedOptions);
      }
    } else {
      result.push(gifts.selectVariantString || this.dataset.selectVariantText || "");
    }
    return result;
  };
  isGiftOptionAvailable_fn = function (item, name, value) {
    if (item.availableOptionValues && item.availableOptionValues[name]) {
      return item.availableOptionValues[name].includes(value);
    }
    return false;
  };
  isGiftOptionSelected_fn = function (item, name, value) {
    if (item.selectedOptions && item.selectedOptions[name] !== undefined) {
      return item.selectedOptions[name] === value;
    }
    return false;
  };
  setGiftSwitchOption_fn = function (item, name, value) {
    if (!item.selectedOptions) item.selectedOptions = {};

    // 1. Set currently selected option
    item.selectedOptions[name] = value;

    // 2. Clear dependent "lower-level" options
    const optionNames = item.options.map((opt) => opt.name);
    const changedIndex = optionNames.indexOf(name);

    for (let i = changedIndex + 1; i < optionNames.length; i++) {
      delete item.selectedOptions[optionNames[i]];
    }

    // 3. Always execute these two steps to ensure data flow consistency
    __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setFreeGiftVidValue_fn).call(this);
    __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setFreeGiftPossibleOptionValues_fn).call(this);
  };
  isGiftValidAndSelected_fn = function (gift) {
    for (let i = 0; i < gift.products.length; i++) {
      if (gift.products[i].selected && gift.products[i].vid !== null) {
        return true;
      }
    }
    return false;
  };
  replaceFreeGiftImage_fn = function (item, vid) {
    let variant = item.product.variants.find((el) => el.id === vid);
    if (typeof variant !== "undefined" && variant !== null) {
      if (variant.featured_image !== "undefined" && variant.featured_image !== null) {
        item.product.featured_image = variant.featured_image.src;
      }
    }
  };
  setFreeGiftVidValue_fn = function () {
    const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
    for (let i = 0; i < gifts.length; i++) {
      for (let j = 0; j < gifts[i].products.length; j++) {
        const item = gifts[i].products[j];
        if (item.product.variants.length === 1) {
          item.vid = item.product.variants[0].id;
        } else {
          let vid = null;

          if (!item.selectedOptions) item.selectedOptions = {};

          const matchedVariants = item.product.variants.filter((variant) => {
            return item.options.every((opt, index) => {
              const selectedValue = item.selectedOptions[opt.name];
              if (selectedValue === undefined) return true; // Unselected options do not participate in current filtering
              const variantOptionKey = `option${index + 1}`;
              return variant[variantOptionKey] === selectedValue;
            });
          });

          if (matchedVariants.length === 1 && Object.keys(item.selectedOptions).length === item.options.length) {
            vid = matchedVariants[0].id;
          }

          if (vid && item.vid !== vid) {
            item.switcherVisible = false;
            __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, replaceFreeGiftImage_fn).call(this, item, vid);
          }
          item.vid = vid;
        }
      }
    }
    __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, unlockFreeGiftElemets_fn).call(this);
  };
  setFreeGiftPossibleOptionValues_fn = function () {
    const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
    for (let i = 0; i < gifts.length; i++) {
      for (let j = 0; j < gifts[i].products.length; j++) {
        const item = gifts[i].products[j];
        if (item.product.variants.length > 1) {
          calculateAvailableOptionsForItem(item);
        }
      }
    }
  };
  unlockFreeGiftElemets_fn = function () {
    setTimeout(() => {
      const el = this.querySelectorAll(".freeGiftModalWrapper");
      el.forEach((item) => {
        item.style.display = "";
      });
      const elPresent = document.getElementById("crazyrocket-launch-icon");
      if (elPresent) elPresent.style.zIndex = "0";
    }, 0);
  };
  addFreeGiftToCart_fn = async function (gift) {
    gift.modalVisible = false;
    let selectedGift = gift.products.find((el) => el.vid !== null && el.selected);
    if (!selectedGift) {
      __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
      return;
    }
    beginFreeGiftMutation();
    try {
      const addResponse = await window._erj_.fetchCartMutation(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: selectedGift.vid, quantity: 1, properties: { _gift: "free_gift" } }],
        }),
      });
      const addResult = await addResponse.json();
      if (!addResponse.ok) {
        throw new Error(addResult?.description || addResult?.message || "Failed to add free gift to cart");
      }
      if (window.__enrouteFreeGiftApi && typeof window.__enrouteFreeGiftApi.requestCartUpdate === "function") {
        window.__enrouteFreeGiftApi.requestCartUpdate(addResult.items);
      } else {
        window.dispatchEvent(new CustomEvent("free-gift:update"));
      }
    } catch (error) {
      console.error("addFreeGiftToCart failed:", error);
      throw error;
    } finally {
      endFreeGiftMutation();
    }
  };
  closeGiftModal_fn = function (gift) {
    if (!gift) return;
    gift.modalVisible = false;
  };
  getGiftByIndex_fn = function (index) {
    const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
    return gifts[index];
  };
  getItemByIndex_fn = function (gift, index) {
    if (!gift || !Array.isArray(gift.products)) return null;
    return gift.products[index];
  };
  handleClick_fn = function (event) {
    const closeTarget = event.target.closest("[data-fgm-close]");
    if (closeTarget) {
      event.preventDefault();
      const gift = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftByIndex_fn).call(this, Number(closeTarget.dataset.giftIndex));
      if (gift) {
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, closeGiftModal_fn).call(this, gift);
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
      }
      return;
    }
    const optionTarget = event.target.closest("[data-fgm-option]");
    if (optionTarget) {
      event.preventDefault();
      const gift = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftByIndex_fn).call(this, Number(optionTarget.dataset.giftIndex));
      const item = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getItemByIndex_fn).call(this, gift, Number(optionTarget.dataset.itemIndex));
      if (item) {
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setGiftSwitchOption_fn).call(this, item, optionTarget.dataset.optionName, optionTarget.dataset.optionValue);
        // refresh view
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
      }
      return;
    }
    const toggleTarget = event.target.closest("[data-fgm-toggle]");
    if (toggleTarget) {
      event.preventDefault();
      const gift = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftByIndex_fn).call(this, Number(toggleTarget.dataset.giftIndex));
      const item = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getItemByIndex_fn).call(this, gift, Number(toggleTarget.dataset.itemIndex));
      if (item) {
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, toggleGiftVariantSwither_fn).call(this, item);
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
      }
      return;
    }
    const confirmTarget = event.target.closest("[data-fgm-confirm]");
    if (confirmTarget) {
      event.preventDefault();
      const gift = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftByIndex_fn).call(this, Number(confirmTarget.dataset.giftIndex));
      if (gift && __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, isGiftValidAndSelected_fn).call(this, gift)) {
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, addFreeGiftToCart_fn)
          .call(this, gift)
          .catch((error) => {
            console.error("Failed to add free gift:", error);
          })
          .finally(() => {
            __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, render_fn).call(this);
          });
      }
      return;
    }
    const itemTarget = event.target.closest("[data-fgm-item]");
    if (itemTarget) {
      const giftIndex = Number(itemTarget.dataset.giftIndex);
      const gift = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftByIndex_fn).call(this, giftIndex);
      const item = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getItemByIndex_fn).call(this, gift, Number(itemTarget.dataset.itemIndex));
      if (gift && item) {
        const variantId = itemTarget.dataset.fgmVariantId ? Number(itemTarget.dataset.fgmVariantId) : null;
        if (gift.benefitType === "variant_set" && variantId) {
          const targetVariant = item.product && Array.isArray(item.product.variants)
            ? item.product.variants.find((variant) => variant.id === variantId)
            : null;
          if (targetVariant) {
            if (!item.selectedOptions) item.selectedOptions = {};
            if (Array.isArray(item.product.options)) {
              item.product.options.forEach((optionName, optionIndex) => {
                const optionValue = targetVariant[`option${optionIndex + 1}`];
                if (optionName && optionValue) {
                  item.selectedOptions[optionName] = optionValue;
                }
              });
            }
            item.vid = variantId;
          }
        }
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setGiftSelected_fn).call(this, gift, item);
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setFreeGiftVidValue_fn).call(this);
        __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, setFreeGiftPossibleOptionValues_fn).call(this);
        
        const itemNodes = this.querySelectorAll(`[data-fgm-item][data-gift-index="${giftIndex}"]`);
        itemNodes.forEach((node) => {
          const idx = Number(node.dataset.itemIndex);
          const currentItem = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getItemByIndex_fn).call(this, gift, idx);
          if (currentItem) {
            const rowVariantId = node.dataset.fgmVariantId ? Number(node.dataset.fgmVariantId) : null;
            const isVariantRowSelected = gift.benefitType === "variant_set" && rowVariantId
              ? currentItem.selected && currentItem.vid === rowVariantId
              : currentItem.selected;
            const titleNode = node.querySelector('.freeGiftModalCasesItemContentMiddleTitle');
            if (titleNode) {
              if (isVariantRowSelected) {
                titleNode.classList.add('freeGiftActiveItem');
              } else {
                titleNode.classList.remove('freeGiftActiveItem');
              }
            }
            const rightNode = node.querySelector('.freeGiftModalCasesItemContentRight');
            if (rightNode) {
              if (isVariantRowSelected) {
                rightNode.innerHTML = `<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="11" fill="black"/>
                    <path d="M6 11.5L9.5 15L16 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>`;
              } else {
                rightNode.innerHTML = `<div class="hvg-circle-1"></div>`;
              }
            }
          }
        });

        const confirmBtn = this.querySelector(`[data-fgm-confirm][data-gift-index="${giftIndex}"]`);
        if (confirmBtn) {
          const valid = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, isGiftValidAndSelected_fn).call(this, gift);
          if (valid) {
            confirmBtn.classList.remove('freeGiftModal-btn--disabled');
          } else {
            confirmBtn.classList.add('freeGiftModal-btn--disabled');
          }
        }
      }
    }
  };

  render_fn = function () {
    const gifts = typeof freeGiftObject !== "undefined" && Array.isArray(freeGiftObject) ? freeGiftObject : [];
    const unlockText = this.dataset.unlockText || "";
    const pickText = this.dataset.pickText || "";
    const confirmText = this.dataset.confirmText || "";
    const visibleGifts = gifts.map((gift, giftIndex) => ({ gift, giftIndex })).filter((item) => item.gift && item.gift.modalVisible);
    if (!visibleGifts.length) {
      this.innerHTML = "";
      return;
    }
    const html = visibleGifts
      .map(({ gift, giftIndex }) => {
        const isVariantSet = gift.benefitType === 'variant_set';
        const products = Array.isArray(gift.products) ? gift.products : [];
        const renderItems = isVariantSet
          ? products.flatMap((item, itemIndex) => {
              const variants = item && item.product && Array.isArray(item.product.variants) ? item.product.variants : [];
              if (!variants.length) {
                return [{ item, itemIndex, variant: null }];
              }
              return variants.map((variant) => ({ item, itemIndex, variant }));
            })
          : products.map((item, itemIndex) => ({ item, itemIndex, variant: null }));
        const itemsHtml = renderItems
          .map(({ item, itemIndex, variant }) => {
            const variantDisplayName =
              variant && variant.title
                ? variant.title
                : item &&
                  item.product &&
                  Array.isArray(item.product.variants) &&
                  item.product.variants[0] &&
                  item.product.variants[0].title
                ? item.product.variants[0].title
                : "";
            const shouldUseVariantTitle =
              isVariantSet && variantDisplayName !== "Default Title";
            const itemTitle =
              shouldUseVariantTitle && variantDisplayName
                ? `${item.product.title} - ${variantDisplayName}`
                : item.product.title;
            const optionDisplayValues = isVariantSet && variant
              ? [variant.option1, variant.option2, variant.option3].filter(Boolean)
              : __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getGiftSelectedOption_fn).call(this, item);
            const selectedOptions = optionDisplayValues
              .map((option) => `<span>${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, option)}</span>`)
              .join("");
            const isCurrentItemSelected =
              isVariantSet && variant
                ? item.selected && item.vid === variant.id
                : item.selected;
            const switcherHtml =
              item.product.variants.length > 1
                ? `
        <div class="freeGiftModalCasesItemContentMiddleSwitcher">
          <div class="gift-cart-switcher-row">
            <div style="width: 100%;">
              <div class="gift-hvg-cart-variant-switcher">
                <div class="gift-hvg-cart-variant-switcher-info" ${!isVariantSet ? `data-fgm-toggle data-gift-index="${giftIndex}" data-item-index="${itemIndex}"` : `style="cursor: default;"`}>
                  <div class="gift-hvg-cart-variant-switcher-info-items">${selectedOptions}</div>
                  ${!isVariantSet ? `
                  <div class="hvg-cart-variant-switcher-info-symbol-img">
                    <svg width="10" height="10" viewBox="0 0 128 128" preserveAspectRatio="none" fill="none" aria-hidden="true" focusable="false"><path d="M6 35 64 92 122 35" stroke="#020202" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </div>` : ""}
                </div>
                ${
                  (item.switcherVisible && !isVariantSet)
                    ? `
                  <div class="gift-hvg-cart-variant-switcher-popup">
                    ${item.options
                      .map(
                        (option) => `
                      <div class="gift-hvg-cart-variant-switcher-popup-optionblock">
                        <div class="gift-hvg-cart-variant-switcher-popup-optionblock-item-title">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, option.name)}</div>
                        ${option.values
                          .map((value) => {
                            const available = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, isGiftOptionAvailable_fn).call(this, item, option.name, value);
                            const selected = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, isGiftOptionSelected_fn).call(this, item, option.name, value);
                            return `
                            <div class="gift-hvg-cart-variant-switcher-popup-optionblock-item">
                              ${
                                available
                                  ? `<div data-fgm-option data-gift-index="${giftIndex}" data-item-index="${itemIndex}" data-option-name="${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, option.name)}" data-option-value="${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, value)}" class="gift-hvg-cart-variant-switcher-popup-optionblock-item-name">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, value)}</div>`
                                  : `<div class="gift-hvg-cart-variant-switcher-popup-optionblock-item-name-disabled">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, value)}</div>`
                              }
                              ${selected ? `<div class="gift-hvg-cart-variant-switcher-popup-optionblock-item-check"></div>` : ""}
                            </div>
                          `;
                          })
                          .join("")}
                      </div>
                    `,
                      )
                      .join("")}
                  </div>`
                    : ""
                }
              </div>
            </div>
          </div>
        </div>`
                : "";
            return `
        <div class="freeGiftModalCasesItem" data-fgm-item data-gift-index="${giftIndex}" data-item-index="${itemIndex}" ${variant && variant.id ? `data-fgm-variant-id="${variant.id}"` : ""}>
          <div class="freeGiftModalCasesItemImage">
            <img src="${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, getOptimizedImageSize_fn).call(this, item.product.featured_image, 80)}" alt="">
          </div>
          <div class="freeGiftModalCasesItemContent">
            <div class="freeGiftModalCasesItemContentMiddle">
              <div class="freeGiftModalCasesItemContentMiddleTitle ${isCurrentItemSelected ? "freeGiftActiveItem" : ""}">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, itemTitle)}</div>
              ${switcherHtml}
            </div>
          </div>
          ${renderItems.length > 1 ? `
          <div class="freeGiftModalCasesItemContentRight">
            ${
              isCurrentItemSelected
                ? `<svg width="17" height="17" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="11" fill="black"/>
                    <path d="M6 11.5L9.5 15L16 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>`
                : `<div class="hvg-circle-1"></div>`
            }
          </div>` : ""}
        </div>
      `;
          })
          .join("");
        const valid = __privateMethodFreeGiftModal(this, _FreeGiftModals_instances, isGiftValidAndSelected_fn).call(this, gift);
        return `
      <div class="freeGiftModalWrapper">
        <div class="freeGiftModal">
          <h3 class="freeGiftModal-header">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, unlockText)}</h3>
          <h3 class="freeGiftModal-subheader">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, pickText)}</h3>
          <div class="freeGiftModalCases">${itemsHtml}</div>
          <a href="#" data-fgm-confirm data-gift-index="${giftIndex}" class="btn freeGiftModal-btn ${valid ? "" : "freeGiftModal-btn--disabled"}">${__privateMethodFreeGiftModal(this, _FreeGiftModals_instances, escapeHtml_fn).call(this, confirmText)}</a>
        </div>
      </div>
    `;
      })
      .join("");
    this.innerHTML = html;
  };
  if (!window.customElements.get("free-gift-modals")) {
    window.customElements.define("free-gift-modals", FreeGiftModals);
  }
  function __privateMethodFreeGiftModal(obj, set, fn) {
    if (!set.has(obj)) {
      throw new TypeError("Cannot access private method");
    }
    return fn;
  }

  function emitFreeGiftUpdate() {
    window.dispatchEvent(new CustomEvent("free-gift:update"));
  }

  function initializeAndShowFreeGiftModal(gifts, index) {
    const modalElement = document.querySelector("free-gift-modals");
    if (modalElement && _FreeGiftModals_instances.has(modalElement)) {
      // 1. Initialize variants and available options
      __privateMethodFreeGiftModal(modalElement, _FreeGiftModals_instances, setFreeGiftVidValue_fn).call(modalElement);
      __privateMethodFreeGiftModal(modalElement, _FreeGiftModals_instances, setFreeGiftPossibleOptionValues_fn).call(modalElement);
    }

    // 2. If there is only one selectable gift for this tier, select it by default
    if (gifts[index].products.length === 1) {
      gifts[index].products[0].selected = true;
    }

    // 3. Check if there are other modals currently open. If not, activate the modal for the current tier
    const isActive = gifts.some((gift) => gift.modalVisible);
    if (!isActive && gifts[index]) {
      gifts[index].modalVisible = true;
      // 4. Unlock/show modal DOM elements
      if (modalElement && _FreeGiftModals_instances.has(modalElement)) {
        __privateMethodFreeGiftModal(modalElement, _FreeGiftModals_instances, unlockFreeGiftElemets_fn).call(modalElement);
      }
    }
  }

  function removeFreeGiftFromCart(gift) {
    if (!gift) return;
    beginFreeGiftMutation();
    window._erj_.fetchCartMutation(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: gift.key, quantity: 0 }),
    }).then(() => {
      if (window.__enrouteFreeGiftApi && typeof window.__enrouteFreeGiftApi.requestCartUpdate === "function") {
        window.__enrouteFreeGiftApi.requestCartUpdate();
      }
      emitFreeGiftUpdate();
    }).catch((error) => {
      console.error("removeFreeGiftFromCart failed:", error);
    }).finally(endFreeGiftMutation);
  }

  function removeFreeGiftsFromCartBatch(gifts) {
    if (!Array.isArray(gifts) || !gifts.length) {
      emitFreeGiftUpdate();
      return;
    }

    const updates = {};
    gifts.forEach((gift) => {
      if (gift && gift.key) {
        updates[gift.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      emitFreeGiftUpdate();
      return;
    }

    beginFreeGiftMutation();
    window._erj_.fetchCartMutation(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    }).then(() => {
      if (window.__enrouteFreeGiftApi && typeof window.__enrouteFreeGiftApi.requestCartUpdate === "function") {
        window.__enrouteFreeGiftApi.requestCartUpdate();
      }
      emitFreeGiftUpdate();
    }).catch((error) => {
      console.error("removeFreeGiftsFromCartBatch failed:", error);
    }).finally(endFreeGiftMutation);
  }

  function adjustFreeGiftQuanitity(gift) {
    if (!gift) return;
    beginFreeGiftMutation();
    window._erj_.fetchCartMutation(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: gift.key, quantity: 1 }),
    }).then(() => {
      if (window.__enrouteFreeGiftApi && typeof window.__enrouteFreeGiftApi.requestCartUpdate === "function") {
        window.__enrouteFreeGiftApi.requestCartUpdate();
      }
      emitFreeGiftUpdate();
    }).catch((error) => {
      console.error("adjustFreeGiftQuanitity failed:", error);
    }).finally(endFreeGiftMutation);
  }

  // recalculate free gifts
  function runFreeGiftsByEvent(event) {
    const gifts = freeGiftObject || [];
    const cart = event && event.detail && event.detail.cart ? event.detail.cart : null;
    if (!cart || !Array.isArray(cart.items)) {
      emitFreeGiftUpdate();
      return;
    }
    if (!gifts.length) {
      const staleFreeGifts = cart.items.filter((item) => item.properties && item.properties._gift === "free_gift");
      removeFreeGiftsFromCartBatch(staleFreeGifts);
      return;
    }

    const cartSum = getEligibleCartSum(cart);
    const currentCartTotal = cartSum / 100.0;
    const currentCartCount = getEligibleCartCount(cart);

    const validCandidateIds = new Set();
    gifts.forEach((gift) => {
      const isCountType = gift.thresholdType === "count";
      const isThresholdMet = isCountType ? gift.edge <= currentCartCount : gift.edge <= currentCartTotal;
      if (isThresholdMet) {
        gift.products.forEach((p) => validCandidateIds.add(p.product.id));
      }
    });

    cart.items.forEach((item) => {
      if (item.properties && item.properties._gift === "free_gift") {
        if (!validCandidateIds.has(item.product_id)) {
          removeFreeGiftFromCart(item);
        }
      }
    });

    for (let i = 0; i < gifts.length; i++) {
      let hasGiftInCart = false;
      const isCountType = gifts[i].thresholdType === "count";
      const isThresholdMet = isCountType ? gifts[i].edge <= currentCartCount : gifts[i].edge <= currentCartTotal;

      if (!isThresholdMet) continue;

      for (let j = 0; j < gifts[i].products.length; j++) {
        const candidate = gifts[i].products[j];
        const giftInCart = cart.items.find((item) => item.properties && item.properties._gift === "free_gift" && item.product_id === candidate.product.id);

        if (giftInCart) {
          // Threshold met and already in cart: mark as added to cart and check quantity
          hasGiftInCart = true;
          const quantity = typeof giftInCart.q !== "undefined" ? giftInCart.q : giftInCart.quantity;
          if (quantity > 1) {
            adjustFreeGiftQuanitity(giftInCart);
          }
        }
      }

      // Core logic fix: only open the modal if [threshold met] AND [no gift from this tier in cart]
      if (!hasGiftInCart) {
        initializeAndShowFreeGiftModal(gifts, i);
      }
    }

    emitFreeGiftUpdate();
  }

  // --- Free-gift recalc: re-entrancy / burst protection --------------------
  // `enroute:cart-updated` now drives free-gift recalculation, but a recalc can
  // itself mutate the cart (add / remove / adjust gifts) and every such mutation
  // dispatches another `enroute:cart-updated`. Without a guard the handler would
  // re-enter itself and race on bursts. So we (1) debounce bursts into a single
  // run and (2) skip a scheduled run while a gift-triggered cart refresh is
  // still in flight — that refresh's own `enroute:cart-updated` supplies the
  // follow-up validating run once the counter drains back to zero.
  var FREE_GIFT_RUN_DEBOUNCE_MS = 200;
  var freeGiftRunTimer = null;
  var freeGiftLatestEvent = null;
  var freeGiftMutationsInFlight = 0;

  function beginFreeGiftMutation() {
    freeGiftMutationsInFlight++;
  }
  function endFreeGiftMutation() {
    if (freeGiftMutationsInFlight > 0) freeGiftMutationsInFlight--;
  }
  function scheduleFreeGiftsRun(event) {
    // Skip gift recalculation during offerSwitch and cancel pending runs to avoid
    // interfering with the offer switch.
    if (event?.detail?.offerSwitch || window._erj_?.offerSwitchInProgress) {
      if (freeGiftRunTimer) clearTimeout(freeGiftRunTimer);
      freeGiftRunTimer = null;
      freeGiftLatestEvent = null;
      return;
    }
    if (event?.detail?.optimistic) return;
    freeGiftLatestEvent = event;
    if (freeGiftRunTimer) clearTimeout(freeGiftRunTimer);
    freeGiftRunTimer = setTimeout(function () {
      freeGiftRunTimer = null;
      // A gift mutation is settling; its resulting cart-updated will re-schedule
      // us with the corrected cart, so skip this (about-to-be-stale) pass.
      // Also skip gift recalculation if an offer switch started during the debounce delay.
      if (freeGiftMutationsInFlight > 0 || window._erj_?.offerSwitchInProgress) return;
      var ev = freeGiftLatestEvent;
      freeGiftLatestEvent = null;
      runFreeGiftsByEvent(ev);
    }, FREE_GIFT_RUN_DEBOUNCE_MS);
  }

  window.addEventListener("enroute:cart-updated", scheduleFreeGiftsRun);
})();

(function () {})();
